import { useEffect, useState } from 'react';
import customAxios from '../../util/Api';
import authHeader from '../../../../services/auth/jwt/auth-header';
import { useDispatch } from 'react-redux';
import { FETCH_ERROR, FETCH_START, FETCH_SUCCESS } from '../../constants/ActionTypes';
import { useToast } from 'context/tostifyContext';
import { updateRecordModifiedDate } from 'routes/Pages/util/CommonAPIs';
import { useSendSageContractNotification } from 'routes/Pages/SiteContract/CustomHooks/useSendSageContractNotification';

const useDefaultSiteVersion = onSuccessCallback => {
  const [open, setOpen] = useState(false); // Site ID dialog
  const [confirmOpen, setConfirmOpen] = useState(false); // Confirmation dialog
  const [siteAssessmentList, setSiteAssessmentList] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const { showToast } = useToast();

  const { sendSageContractNotification } = useSendSageContractNotification();

  const openVersionDialog = () => setOpen(true);
  const closeVersionDialog = () => {
    setOpen(false);
    // setSelectedSiteId('');
  };

  const fetchSiteAssessmentIds = async () => {
    try {
      dispatch({ type: FETCH_START });
      const res = await customAxios.get('/CRM/GetCRMSiteDetails', {
        headers: authHeader(),
      });

      if (res.status === 200 && Array.isArray(res.data)) {
        const siteIds = res.data.map(item => {
          return item.siteAssessmentID + ' : ' + item.siteDetailsRef;
        });
        setSiteAssessmentList(siteIds);
        dispatch({ type: FETCH_SUCCESS });
      } else {
        throw new Error('Unexpected response format');
      }
    } catch (err) {
      dispatch({ type: FETCH_ERROR, payload: 'Failed to load site assessment IDs' });
      showToast('Failed to fetch site assessment data', 'error');
    }
  };
  const checkAndCreate = async () => {
    if (!selectedSiteId) return;
    setLoading(true);

    try {
      const res = await customAxios.get(`/Ops/CheckDefaultTimberPricing/${selectedSiteId}`, {
        headers: authHeader(),
      });

      const exists = res.data.result === 1;

      if (exists) {
        closeVersionDialog();
        setTimeout(() => setConfirmOpen(true), 300);
      } else {
        await createDefaultVersion();
      }
    } catch (err) {
      showToast('Error checking default version', 'error');
      dispatch({ type: FETCH_ERROR, payload: 'Check failed' });
    } finally {
      setLoading(false);
    }
  };

  const createDefaultVersion = async () => {
    try {
      dispatch({ type: FETCH_START });
      const res = await customAxios.post(
        '/Ops/AddDefaultTimberPricing',
        { siteAssessmentID: selectedSiteId },
        { headers: authHeader() },
      );

      if (res.data.includes('missing Product details')) {
        showToast(res.data, 'error');
      } else {
        showToast(res.data);
      }

      closeVersionDialog();
      setConfirmOpen(false);
      if (onSuccessCallback) onSuccessCallback();
      dispatch({ type: FETCH_SUCCESS });
    } catch (err) {
      showToast('Error creating default version', 'error');
      dispatch({ type: FETCH_ERROR, payload: 'Create failed' });
    }
  };

  const confirmDeleteAndCreate = async () => {
    try {
      dispatch({ type: FETCH_START });

      await customAxios.post(
        '/Ops/DeleteTimberPricing',
        {
          siteAssessmentID: selectedSiteId,
          pricingVersion: `${selectedSiteId}-0`,
        },
        { headers: authHeader() },
      );

      await createDefaultVersion();
    } catch (err) {
      showToast('Error deleting version', 'error');
      dispatch({ type: FETCH_ERROR, payload: 'Delete failed' });
    }
  };

  useEffect(() => {
    fetchSiteAssessmentIds();
  }, []);

  async function handleAddAsAddendum(pricingVersion, spNumber) {
    if (spNumber) {
      showToast(`${pricingVersion} Can not be added as Addendum`, 'error');
      return;
    }

    const siteId = pricingVersion.split('-')[0];
    const res = await customAxios.get(`CRM/GetCRMSiteDetails?siteAssessmentId=${siteId}`, {
      headers: authHeader(),
    });

    const whNumber = res.data[0].wH_Number;

    if (!whNumber) {
      showToast(`Can not find Contract Number`, 'error');
      return;
    }

    try {
      const payload = {
        contractUniqueID: whNumber,
        newTimberPricingVersion: pricingVersion,
      };

      const res = await customAxios.post('/Ops/UpdateAddendumToContract', payload, { headers: authHeader() });

      if (res.status === 200) {
        showToast(`${pricingVersion} added as Addendum successfully`);
        await updateRecordModifiedDate(siteId);

        const contract = await customAxios.get(`/Ops/GetContracts?contractUniqueID=${whNumber}`, { headers: authHeader() });

        const rD = {
          ...contract?.data?.[0],
          pricingVersion,
        };
        await sendSageContractNotification(`Timber Pricing Addendum – ${whNumber}`, whNumber, rD, true);
      }

      console.log(payload, 'data');
    } catch (err) {
      console.log(err);
      showToast(`Error for Added as Addendum`);
    }
  }

  return {
    open,
    confirmOpen,
    openVersionDialog,
    closeVersionDialog,
    setConfirmOpen,
    siteAssessmentList,
    selectedSiteId,
    setSelectedSiteId,
    checkAndCreate,
    confirmDeleteAndCreate,
    loading,
    handleAddAsAddendum,
  };
};

export default useDefaultSiteVersion;
